/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN HOMEWORK MASTER API
 * Role: Admin
 * Scope: Tenant-based Homework Distribution & Tracking Management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */

const ADMIN_HOMEWORK_MASTER_ENDPOINT =
  "/admin/homework/homework_master.php";

/* =============================================================
   GET – Fetch Homework Masters
   Supports: pagination, search
============================================================= */

export const getAdminHomeworkMasters = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_HOMEWORK_MASTER_ENDPOINT,
      null,
      "Admin - Fetch Homework Masters",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message || "Failed to fetch homework records",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error(
      "Admin Fetch Homework Masters Error:",
      error
    );

    return {
      success: false,
      message:
        error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Homework Model (Includes Roster States)
============================================================= */

export const getAdminHomeworkMasterById = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Homework Master ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_HOMEWORK_MASTER_ENDPOINT}?id=${id}`,
      null,
      "Admin - Fetch Homework Master By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to fetch homework master data",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error(
      "Admin Fetch Homework Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create & Distribute Homework (Group / Individual Scope)
============================================================= */

export const createAdminHomeworkMaster = async (
  payload,
  options = {}
) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_HOMEWORK_MASTER_ENDPOINT,
      payload,
      "Admin - Create Homework Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to create and distribute homework",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Homework assigned and distributed successfully",
    };
  } catch (error) {
    console.error(
      "Admin Create Homework Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Homework Definition & Deadline
============================================================= */

export const updateAdminHomeworkMaster = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Homework Master ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_HOMEWORK_MASTER_ENDPOINT}?id=${id}`,
      payload,
      "Admin - Update Homework Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to update homework metrics",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Homework metrics updated successfully",
    };
  } catch (error) {
    console.error(
      "Admin Update Homework Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Cascade Delete Homework Allocation Footprints
============================================================= */

export const deleteAdminHomeworkMaster = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Homework Master ID",
      };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_HOMEWORK_MASTER_ENDPOINT}?id=${id}`,
      null,
      "Admin - Delete Homework Master",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to clear homework subsystem footprint",
      };
    }

    return {
      success: true,
      message:
        res.message ||
        "Homework profile dropped cleanly",
    };
  } catch (error) {
    console.error(
      "Admin Delete Homework Master Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};